import type { Events, SportsApiClient } from '@collector/sportsapi-client'
import {
  SoccerSubParticipantStatId,
  type Event,
  type Participant,
  type Stat,
  type StatId,
  type SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import type { SubParticipant } from '@collector/sportsapi-types'

export function cloneWithStats(player: SubParticipant): SubParticipant {
  return { ...player, stats: player.stats?.map((s) => ({ ...s })) || [] }
}

function getStatIndex(player: SubParticipant, statId: StatId): number {
  if ('stats' in player)
    return player.stats.findIndex(({ id }) => id === (statId as number))
  else return -1
}

export function getStat(player: SubParticipant, statId: StatId): Stat | null {
  const index = getStatIndex(player, statId)
  if (index >= 0) return player.stats[index]
  else return null
}

function setStat(
  player: SubParticipant,
  id: StatId,
  value: number | string | null,
): void {
  const statIndex = getStatIndex(player, id)
  const updatedStat = { id, value }
  if (statIndex >= 0) player.stats.splice(statIndex, 1, updatedStat)
  else if ('stats' in player) player.stats?.push(updatedStat)
  else (player as SubParticipant).stats = [updatedStat]
}

function getCapitanStat(player: SubParticipant): Stat | null {
  return getStat(player, SoccerSubParticipantStatId.Captain)
}

export function isCaptain(player: SubParticipant): boolean {
  return getCapitanStat(player)?.value === 1
}

export function getUpdateLineupsPlayerPayload(
  player: SubParticipant,
  position: SubParticipantPosition,
): Events.SubParticipantsSave.SubParticipantsSavePostSquad {
  return {
    id: player.id ?? undefined,
    short_name: player.id ? undefined : player.short_name,
    shirt_nr: player.shirt_nr === null ? undefined : player.shirt_nr,
    position,
    captain: isCaptain(player),
    position_reason_code: player.position_reason_code ?? undefined, // backward compatibility - position_reason_code will be removed in API
    position_reason_id: player.position_reason_code
      ? undefined
      : (player.position_reason_id ?? undefined),
    position_detailed_reason_id:
      player.position_detailed_reason_id ?? undefined,
  }
}

export function getUpdateLineupsPayload(
  players: SubParticipant[],
  position: SubParticipantPosition,
): Events.SubParticipantsSave.SubParticipantsSavePostSquad[] {
  return players.map((player) =>
    getUpdateLineupsPlayerPayload(player, position),
  )
}

export function updateLineups(
  sportsApiClient: SportsApiClient,
  eventId: Event['id'],
  participantId: Participant['id'],
  squad: Events.SubParticipantsSave.SubParticipantsSavePostSquad[],
): Promise<unknown> {
  return sportsApiClient.Events.SubParticipantsSave.post(
    eventId,
    participantId,
    squad,
  )
}

export function setCaptainStat(
  player: SubParticipant,
  value: number | string | null,
): void {
  return setStat(player, SoccerSubParticipantStatId.Captain, value)
}

export function setCaptain(player: SubParticipant): void {
  return setCaptainStat(player, 1)
}

export function unsetCaptain(player: SubParticipant): void {
  return setCaptainStat(player, 0)
}

export function isPlayerSame(a: SubParticipant, b: SubParticipant): boolean {
  if ('lineup_id' in a && 'lineup_id' in b) return a.lineup_id === b.lineup_id
  else return a.id === b.id
}
